import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "fileInput",
    "defaultImage",
    "clearImages",
    "previewImage",
    "currentImageLabel",
    "saveButton",
    "cancelButton",
    "clearButton",
  ]

  static values = {
    loginImageDefault: String,
    loginImage: String,
    defaultImages: Object
  }

  connect() {
    this.loginImageDefault = { current: this.loginImageDefaultValue, new: null }
    this.loginImage        = { current: this.loginImageValue, new: null }

    this.imageSelection = {
      org: this.loginImage.current || null,
      ...this.defaultImagesValue
    }

    this.clearImagesTarget.value = false
    this.#updatePreview()
  }

  ///////////////
  /// Actions ///
  ///////////////

  // Called when a user clicks on a preset or uploaded image
  selectImage(event) {
    const name = event.currentTarget.dataset.name

    if (name !== "org") {
      this.loginImageDefault.new = name
      this.defaultImageTarget.value = name
      this.loginImage.new = null
    } else {
      this.loginImageDefault.new = null
      this.loginImage.new = this.imageSelection.org
    }

    this.#updatePreview()
  }

  // Called when a user uploads a new image file
  uploadImage(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target.result

      this.loginImageDefault.new = null
      this.defaultImageTarget.value = ""
      this.loginImage.new = result
      this.clearImagesTarget.value = "false"

      this.imageSelection.org = result
      this.#updateChoices()
      this.#updatePreview()
    }

    reader.readAsDataURL(file)
  }

  // Called when user clicks "Cancel"
  cancelChange(event) {
    event.preventDefault()

    this.loginImageDefault.new = null
    this.defaultImageTarget.value = ""

    this.loginImage.new = null
    this.fileInputTarget.value = ""

    if (this.loginImage.current) {
      this.imageSelection.org = this.loginImage.current
    } else {
      delete this.imageSelection.org
    }

    this.#toggleActions();
  }

  // Called when user clicks "Clear Image"
  clearImageSelection(event) {
    this.loginImageDefault.new = null
    this.defaultImageTarget.value = ""

    this.loginImage.new = null
    this.clearImagesTarget.value = "true"
    this.saveButtonTarget.classList.add('hidden')
    this.cancelButtonTarget.classList.add('hidden')

    delete this.imageSelection.org
  }

  // Called when user clicks "Save"
  saveImageSelection(event) {
    this.cancelButtonTarget.classList.add('hidden')
    this.clearButtonTarget.classList.add('hidden')
  }

  ///////////////
  /// Helpers ///
  ///////////////

  // Updates the preview image display area
  #updatePreview() {
    let selectedName = null
    let selectedImage = null

    if (this.loginImageDefault.new && this.imageSelection[this.loginImageDefault.new]) {
      selectedName  = this.loginImageDefault.new
      selectedImage = this.imageSelection[selectedName]
    } else if (this.loginImage.new) {
      selectedName = "org"
      selectedImage = this.loginImage.new
    } else if (this.loginImageDefault.current) {
      selectedName = this.loginImageDefault.current
      selectedImage = this.imageSelection[selectedName]
    } else if (this.loginImage.current) {
      selectedName = "org"
      selectedImage = this.loginImage.current
    } else {
      selectedName = this.#getRandomDefaultImageName()
      selectedImage = this.imageSelection[selectedName]
    }

    this.previewImageTarget.style.backgroundImage = `url(${selectedImage})`
    this.#updateImageLabel();
    this.#highlightSelected(selectedName);
    this.#toggleActions();
  }

  // Updates label text showing current image usage info
  #updateImageLabel() {
    if (!this.loginImage.current && !this.loginImageDefault.current) {
      this.currentImageLabelTarget.innerHTML = "You are currently using an Octopi default image."
    } else if (this.loginImage.current) {
      this.currentImageLabelTarget.innerHTML = "You are currently using your organization's uploaded image."
    }
  }

  // Gets a random default image if no other is selected
  #getRandomDefaultImageName() {
    const keys = Object.keys(this.imageSelection).filter(key => key !== 'org')
    return keys[Math.floor(Math.random() * keys.length)];
  }

  // Re-renders the image selection choices
  #updateChoices() {
    const container = this.element.querySelector(".login_page_choices")
    if (!container) return

    container.innerHTML = ""

    if (this.imageSelection.org) container.appendChild(this.#buildChoice('org', this.imageSelection.org))
    Object.entries(this.imageSelection)
      .filter(([name, url]) => name !== 'org')
      .forEach(([name, url]) => container.appendChild(this.#buildChoice(name, url)))
  }

  #buildChoice(name, url) {
    const option = document.createElement("div")
    option.classList.add("login_page_choices__option")
    option.dataset.action = "click->login-image#selectImage"
    option.dataset.name = name
    option.style.backgroundImage = `url(${url})`

    return option;
  }

  // Toggles the <Save>, <Cancel>, <Clear Image> buttons
  #toggleActions() {
    const hasOrgImage = !!this.imageSelection.org
    const hasChanges = this.loginImage.new || this.loginImageDefault.new

    this.saveButtonTarget.classList.toggle('hidden', !hasChanges)
    this.cancelButtonTarget.classList.toggle('hidden', !hasChanges)
    this.clearButtonTarget.classList.toggle('hidden', !hasOrgImage)
  }

  // Visually highlight the selected image by toggling "selected" class
  #highlightSelected(imgName) {
    this.element
      .querySelectorAll(".login_page_choices__option")
      .forEach((el) => el.classList.remove("selected"))

    this.element
      .querySelector(`.login_page_choices__option[data-name=${imgName}]`)
      ?.classList
      ?.add('selected')
  }
}
